import React, { lazy, Suspense, useMemo } from 'react';
import cx from 'classnames';
import { getFlyingBetslipConfig } from 'helpers/prematch';
import PropTypes from 'prop-types';
import { CDN_TYPE_CMS, imageUrlCDN } from 'utils';

import { printMissingConfigError, SiteConfigManager, useSettings } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { Label, Loader } from '@atlas/ui';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { PLATFORM_TYPES } from 'common/constants';
import { LinkComponent } from 'components/NavigationBar/NavigationBarItem';
import NotificationContainer from 'components/NavigationBar/NotificationContainer';

import { navigationTypes } from './constants';

import { getIsPWAInstalled } from 'helpers/getIsPWAInstalled';

const { MOBILE, MULTIPLATFORM } = PLATFORM_TYPES;

const BetslipPopupButton = lazy(
  () => import(/* webpackChunkName: "Betslip" */ 'common/Betslip/BetslipPopupButton'),
);

const NavigationBarItem = ({
  altIcon,
  icon,
  iconType,
  lightIcon,
  name,
  order,
  path,
  withNotification,
}) => {
  const { theme } = useSettings();
  const isDarkMode = theme === 'dark';

  const Icon = useMemo(() => {
    if (iconType === 'custom') {
      if (isDarkMode) {
        return (
          <div className="menu-custom-icon">
            <img alt="menu-item" src={imageUrlCDN(altIcon || lightIcon || icon, CDN_TYPE_CMS)} />
          </div>
        );
      }

      return (
        <div className="menu-custom-icon">
          <img alt="menu-item" src={imageUrlCDN(lightIcon || altIcon || icon, CDN_TYPE_CMS)} />
        </div>
      );
    }

    return <span className={`bottom-${icon}`} />;
  }, [altIcon, icon, iconType, isDarkMode, lightIcon]);

  return (
    <LinkComponent className={cx(`button navigation-item navigation-item_${order}`)} link={path}>
      {Icon}
      <NotificationContainer withNotification={withNotification}>
        <Label className="label sub-title-xsmall-500-text" message={name} />
      </NotificationContainer>
    </LinkComponent>
  );
};

export default function NavigationBar() {
  const sportRoute = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_PREMATCH);
  const liveRoute = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_LIVE);
  const resultsRoute = SiteConfigManager.getComponentPath(COMPONENT_TYPES.RESULTS);
  const pages = SiteConfigManager.getConfig('config.pages', []);
  const navigationList = pages.filter(({ isActive, type }) => isActive && navigationTypes[type]);

  const betslipNavConfig = useMemo(() => getFlyingBetslipConfig(), []);
  const expandedNavigation = SiteConfigManager.getConfig('navigation.mobile', []);

  // state of the betslip popup
  const { betIdToEdit } = useLocalBetslip();
  const { quickBet: isQuickBet } = useSettings();

  const navigationClass = cx('navigation navigation_iframe', 'iframe-betslip-default', {
    shadow: isQuickBet || betIdToEdit,
  });

  const prepareNavigation = {
    results: {
      icon: 'i-results',
      path: resultsRoute,
    },
    sportLive: {
      icon: 'i-live',
      path: liveRoute,
    },
    sportPrematch: {
      icon: 'i-football',
      path: sportRoute,
    },
  };

  const links = useMemo(
    () =>
      expandedNavigation
        .filter(el => el.platform_type === MULTIPLATFORM || el.platform_type === MOBILE)
        .slice(0, betslipNavConfig.hasSimple || betslipNavConfig.hasExtended ? 6 : 5)
        .sort((a, b) => a.order_index - b.order_index)
        .map(
          ({
            alt_icon: altIcon,
            icon,
            icon_type: iconType,
            light_icon: lightIcon,
            location,
            name,
          }) => ({
            altIcon,
            icon,
            iconType,
            lightIcon,
            name,
            path: location,
          }),
        ),
    [betslipNavConfig.hasExtended, betslipNavConfig.hasSimple, expandedNavigation],
  );

  const Menu = useMemo(() => {
    if (betslipNavConfig.hasNewNavigation) {
      return links.map(({ altIcon, icon, iconType, lightIcon, name, path }, index) => (
        <NavigationBarItem
          altIcon={altIcon}
          icon={icon}
          iconType={iconType}
          lightIcon={lightIcon}
          name={name}
          order={index + 1}
          path={path}
        />
      ));
    }

    return navigationList.map(({ path, type }, index) => (
      <NavigationBarItem
        icon={prepareNavigation[type].icon}
        key={path}
        name={navigationTypes[type]}
        order={index + 1}
        path={path}
      />
    ));
  }, [betslipNavConfig.hasNewNavigation, links, navigationList, prepareNavigation]);

  if (pages.length === 0) {
    printMissingConfigError('config.pages');
  }

  if (expandedNavigation.length === 0) {
    printMissingConfigError('navigation.mobile');
  }

  const navBarClass = cx('navigation-tab-bar', { 'is-pwa': getIsPWAInstalled() });

  return (
    <div className={navigationClass}>
      <div className={navBarClass}>
        {Menu}
        {!betslipNavConfig.hasSimple && !betslipNavConfig.hasExtended && (
          <Suspense fallback={<Loader />}>
            <BetslipPopupButton />
          </Suspense>
        )}
      </div>
    </div>
  );
}

NavigationBarItem.propTypes = {
  icon: PropTypes.string,
  iconType: PropTypes.string,
  name: PropTypes.string,
  order: PropTypes.number,
  path: PropTypes.string,
};
